// src/components/SplashScreen.tsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import "./SplashScreen.css";
import clsx from "clsx";


type ImageBackgroundProps = {
  src: string;
  className?: string;
};

/* ---------- Fondo con imagen en vez de video ---------- */
export function ImageBackground({ src, className = "" }: ImageBackgroundProps) {
  const [ready, setReady] = useState(false);

  return (
    <>
      <img
        src={src}
        alt=""
        className={`absolute inset-0 w-full h-full object-cover ${className}`}
        onLoad={() => setReady(true)}
      />
      {ready && <div className="splash-blur-animated" />}
    </>
  );
}

type SplashScreenProps = {
  backgroundUrl: string;
};

// export function SplashScreen({ backgroundUrl }: SplashScreenProps) {

type Props = {
  words: string[];
  /** Usa "auto" para medir según CSS (recomendado para responsive) */
  lineHeight?: number | "auto";
  gap?: number;
  moveDuration?: number;
  pause?: number;
  showNeighbors?: boolean;
  className?: string;

  /** Se dispara una sola vez cuando ya midió (y fonts si aplica) */
  onReady?: () => void;
};


export function WordRotator({
  words,
  lineHeight = "auto",
  gap = 8,
  moveDuration = 0.7,
  pause = 0.2,
  showNeighbors = true,
  className = "text-white text-4xl md:text-6xl font-serif leading-none text-center",
  onReady,
}: Props) {
  const items = [...words, words[0]];
  const controls = useAnimationControls();
  const measurerRef = useRef<HTMLSpanElement>(null);

  const [i, setI] = useState(0);
  const [widths, setWidths] = useState<number[]>([]);
  const [measuredLH, setMeasuredLH] = useState<number>(56); // fallback
  const [fontReady, setFontReady] = useState<boolean>(!(document as any).fonts);
  const [measured, setMeasured] = useState<boolean>(false);
  const ready = fontReady && measured;

  // ✅ avisa cuando esté listo (una sola vez)
  const readyFired = useRef(false);
  useEffect(() => {
    if (!ready || readyFired.current) return;
    readyFired.current = true;
    onReady?.();
  }, [ready, onReady]);

  // Medición (anchos + lineHeight real del DOM)
  useLayoutEffect(() => {
    const measureOnce = () => {
      if (!measurerRef.current) return;
      const el = measurerRef.current;

      // line-height real del texto (depende de tailwind responsive)
      // Usamos el alto de la caja con una palabra representativa.
      el.textContent = words[0] ?? "A";
      const lhRect = el.getBoundingClientRect();
      const lh = Math.max(1, Math.round(lhRect.height)); // px
      if (lineHeight === "auto") setMeasuredLH(lh);

      // medir anchos de cada palabra
      const w: number[] = [];
      for (const word of words) {
        el.textContent = word;
        w.push(Math.ceil(el.getBoundingClientRect().width));
      }
      setWidths(w);
      setMeasured(true);
    };

    // medir ahora y en el próximo frame (por font-swap)
    measureOnce();
    const raf = requestAnimationFrame(measureOnce);

    // fonts.ready
    const fontset: FontFaceSet | undefined = (document as any).fonts;
    let onLoadingDone: (() => void) | null = null;
    if (fontset) {
      fontset.ready.then(() => {
        measureOnce();
        setFontReady(true);
      });
      onLoadingDone = () => {
        measureOnce();
        setFontReady(true);
      };
      (fontset as any).addEventListener?.("loadingdone", onLoadingDone);
    }

    // re-medición en resize y cambios del measurer (breakpoints)
    const onResize = () => measureOnce();
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(() => measureOnce());
    if (measurerRef.current) ro.observe(measurerRef.current);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      if (fontset && onLoadingDone)
        (fontset as any).removeEventListener?.("loadingdone", onLoadingDone);
    };
  }, [words, lineHeight]);

  const lineH = lineHeight === "auto" ? measuredLH : lineHeight;
  const step = lineH + gap;

  // Alturas de viewport y offset
  const H = showNeighbors ? lineH * 3 + gap * 2 : lineH;
  const viewportH = showNeighbors ? H : lineH;
  const baseOffset = showNeighbors ? (viewportH - lineH) / 2 : 0;

  // Loop cuando esté listo
  useEffect(() => {
    if (!ready) return;
    let active = true;
    (async () => {
      await controls.set({ y: baseOffset });
      let idx = 0;
      while (active) {
        await controls.start({
          y: baseOffset - step * idx,
          transition: { duration: moveDuration, ease: [0.22, 0.61, 0.36, 1] },
        });
        await new Promise((r) => setTimeout(r, pause * 1000));
        idx = (idx + 1) % (items.length - 1);
        setI(idx);
        if (idx === 0) await controls.set({ y: baseOffset });
      }
    })();
    return () => {
      active = false;
    };
  }, [ready, controls, items.length, step, moveDuration, pause, baseOffset]);

  const targetWidth = (widths[i] ?? 0) + 4 || 0;

  const fadeMask = showNeighbors
    ? "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,1) 45%, rgba(0,0,0,1) 55%, rgba(0,0,0,0))"
    : "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,1) 5%, rgba(0,0,0,1) 95%, rgba(0,0,0,0))";

  return (
    <motion.div
      className="relative inline-flex justify-center items-center"
      animate={ready ? { width: targetWidth, opacity: 1 } : { opacity: 0 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
      style={{ height: lineH }}
    >
      <div
        className="absolute"
        style={{
          height: H,
          overflow: "hidden",
          WebkitMaskImage: fadeMask,
          maskImage: fadeMask,
        }}
      >
        <motion.div
          animate={controls}
          initial={{ y: baseOffset }}
          style={{
            display: "flex",
            flexDirection: "column",
            gap,
            willChange: "transform",
          }}
          aria-live="polite"
          aria-atomic="true"
        >
          {items.map((w, idx) => {
            const isActive = idx === i;

            return (
              <motion.span
                key={`${w}-${idx}`}
                className={className}
                initial={false}
                animate={{
                  scale: isActive ? 1 : 0.75,
                  opacity: isActive ? 1 : 0.75,
                }}
                transition={{
                  scale: {
                    duration: 0.55,
                    ease: "linear",
                  },
                  opacity: {
                    duration: 0.35,
                    ease: "linear",
                  },
                }}
                style={{
                  height: lineH,
                  whiteSpace: "nowrap",
                  transformOrigin: "center center",
                }}
              >
                {w}
              </motion.span>
            );
          })}
        </motion.div>
      </div>

      {/* Medidor oculto */}
      <span
        ref={measurerRef}
        className={className}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      />
    </motion.div>
  );
}

// --- Ejemplo de uso responsive ---
const WORDS = [
  "Vive",
  "Explora",
  "Come",
  "Viaja",
  "Aprende",
  "Descubre",
  "Prueba",
];

export function SplashScreen({ backgroundUrl }: SplashScreenProps) {
  const [textReady, setTextReady] = useState(false);

  return (
    <motion.div
      className="absolute inset-0 touch-none bg-black flex items-center justify-center"
      exit={{ opacity: 0, scale: 1.3 }}
      transition={{
        opacity: { duration: 0.4, delay: 0.2 },
        scale: { duration: 0.6, ease: "easeInOut" },
      }}
    >
      <ImageBackground src={backgroundUrl} />

      {/* ✅ mismo contenedor, solo lo hacemos aparecer cuando ya midió */}
      <div
        className={clsx(
          "z-10 inline-flex items-center p-safe-3 transition-opacity duration-300",
          textReady ? "opacity-100" : "opacity-0"
        )}
      >
        <WordRotator
          words={WORDS}
          lineHeight="auto"
          gap={8}
          moveDuration={1.1}
          pause={0.7}
          showNeighbors
          className="text-white text-3xl sm:text-4xl font-serif leading-none text-center"
          onReady={() => setTextReady(true)}
        />
        <span className="w-3 sm:w-4" />
        <span className="text-white text-3xl sm:text-4xl whitespace-nowrap font-serif leading-none">
          como un local
        </span>
      </div>
    </motion.div>
  );
}
